import { createSlice } from '@reduxjs/toolkit';
import { format } from 'date-fns';

import { initialState } from '»components/Auth/redux';
import {
  isBfacmActiveClient,
  isHemeraActiveClient,
  isStinvestActiveClient,
} from '»utils/clientsUtils';

const getClassifierOptions = ({
  classifiers,
  currentClassifierId,
  shouldSortAlphabetically = false,
}) => {
  const classifierOptions = classifiers.find(
    (classifier) => classifier.id === currentClassifierId,
  ).options;

  if (shouldSortAlphabetically)
    return classifierOptions.sort((firstOption, secondOption) =>
      firstOption.label.localeCompare(secondOption.label),
    );

  return classifierOptions;
};

const getFieldDefaultValue = (fieldName) => {
  if (fieldName === 'newClient') return 'Y';

  if (
    fieldName === 'profStatus' ||
    (fieldName === 'employeeType' && !isHemeraActiveClient) ||
    (fieldName === 'job' && isStinvestActiveClient) ||
    (fieldName === 'incomeSource' && isStinvestActiveClient) ||
    fieldName === 'relationAssets'
  )
    return [];

  if (fieldName === 'todaysDate') return format(new Date(), 'yyyy-MM-dd');

  return '';
};

const getFieldValue = (field, personInfo) => {
  const { name, value } = field;

  if (name === 'employer') return personInfo.employer ?? '';

  if (name === 'employeeType' && !!personInfo.employer) {
    if (isHemeraActiveClient) return 'TTB_CO';

    return ['TTB_CO'];
  }

  if (name === 'name' || name === 'denomination' || name === 'repName')
    return personInfo.fullHolderName ?? '';

  if (name === 'repJob') return personInfo.job ?? '';

  if (name === 'repNationality' || name === 'otherNationalities')
    return personInfo.nationality ?? '';

  if (name === 'placeOfBirth') return personInfo.naturalness ?? '';

  if (name === 'nif') {
    if (isStinvestActiveClient) {
      if (personInfo.nif) return personInfo.nif;
      if (
        (personInfo.Document?.idType === 'NIFA' ||
          personInfo.Document?.idType === 'BIDENTICIVIL') &&
        personInfo.Document.idNumber
      )
        return personInfo.Document.idNumber;
      return '';
    }
    return personInfo.nif ?? '';
  }

  if (name === 'address' || name === 'street' || name === 'repAddress')
    return personInfo.Address?.address ?? '';

  if (name === 'province') return personInfo.Address?.province ?? '';

  if (name === 'town') return personInfo.Address?.town ?? '';

  if (name === 'idType' || name === 'repIdType') {
    if (isStinvestActiveClient && personInfo.Document?.idType === 'NIFA')
      return '';

    return personInfo.Document?.idType ?? '';
  }

  if (name === 'idNumber' || name === 'repIdNum') {
    if (isStinvestActiveClient && personInfo.Document?.idType === 'NIFA')
      return '';

    return personInfo.Document?.idNumber ?? '';
  }

  if (name === 'idIssueLocal' || name === 'repIdLocal')
    return personInfo.Document?.location ?? '';

  if (name === 'idIssueDate' || name === 'repIdDate')
    return personInfo.Document?.emissionDate
      ? format(new Date(personInfo.Document.emissionDate), 'yyyy-MM-dd')
      : '';

  if (name === 'idExpDate' || name === 'repIdExpDate')
    return personInfo.Document?.idExpDate
      ? format(new Date(personInfo.Document.idExpDate), 'yyyy-MM-dd')
      : '';

  if (name === 'idEntity') return personInfo.Document?.idEntity ?? '';

  if (name === 'phoneNum')
    return (
      personInfo.Contact?.find(
        (contact) =>
          contact.idType === 'TMOVEL' || contact.idType === 'TMOVEL1',
      )?.contact ?? ''
    );

  if (name === 'phoneNum2')
    return (
      personInfo.Contact?.find((contact) => contact.idType === 'TMOVEL2')
        ?.contact ?? ''
    );

  if (name === 'email' || name === 'email1')
    return (
      personInfo.Contact?.find(
        (contact) => contact.idType === 'EMAIL' || contact.idType === 'EMAIL1',
      )?.contact ?? ''
    );

  if (name === 'email2')
    return (
      personInfo.Contact?.find((contact) => contact.idType === 'EMAIL2')
        ?.contact ?? ''
    );

  if (name === 'gender') return personInfo.gender ?? '';

  if (name === 'birthDate')
    return personInfo.dateOfBirth
      ? format(new Date(personInfo.dateOfBirth), 'yyyy-MM-dd')
      : '';

  if (name === 'maritalStatus') return personInfo.maritalStatus ?? '';

  return value;
};

export const onboardingSlice = createSlice({
  name: 'onboardingSlice',
  initialState,
  reducers: {
    clearOnboardingError: (state) => {
      state.onboarding.error = '';
    },
    setOnboardingCurrentStep: (state, action) => {
      const { direction, hasOnboardingInitialOtp } = action.payload;
      const isQuestionnaireAvailable = state.onboarding.shouldShowQuestionnaire;

      if (direction === 'forward') {
        if (state.onboarding.currentStep === 1) {
          if (hasOnboardingInitialOtp) {
            state.onboarding.currentStep += 1;
          } else {
            state.onboarding.currentStep = 3;
          }
        } else if (state.onboarding.currentStep === 3) {
          if (state.onboarding.legalStatus === 'PS' || isHemeraActiveClient) {
            if (isQuestionnaireAvailable) {
              state.onboarding.currentStep = 6;
            } else {
              state.onboarding.currentStep = 7;
            }
          } else if (isBfacmActiveClient) {
            state.onboarding.currentStep = 5;
          } else {
            state.onboarding.currentStep += 1;
          }
        } else if (
          state.onboarding.currentStep === 5 &&
          !isQuestionnaireAvailable
        ) {
          state.onboarding.currentStep = 7;
        } else {
          state.onboarding.currentStep += 1;
        }
      } else if (direction === 'backward') {
        if (
          (state.onboarding.currentStep === 5 && isBfacmActiveClient) ||
          (state.onboarding.currentStep === 6 &&
            (state.onboarding.legalStatus === 'PS' || isHemeraActiveClient))
        ) {
          state.onboarding.currentStep = 3;
        } else if (
          state.onboarding.currentStep === 7 &&
          !isQuestionnaireAvailable
        ) {
          if (state.onboarding.legalStatus === 'PS' || isHemeraActiveClient) {
            state.onboarding.currentStep = 3;
          } else {
            state.onboarding.currentStep = 5;
          }
        } else {
          state.onboarding.currentStep -= 1;
        }
      }
    },
    getMainFormFieldsSuccess: (state, action) => {
      const { dbConfigurations, formPreviousInfo, iban, iban2 } =
        action.payload;
      const fields = dbConfigurations.reduce((acc, field) => {
        const { detailId, value } = field;
        const fieldDetails = value.split(';');

        const newField = {
          name: detailId,
          isHidden: fieldDetails[0] === 'N',
          isRequired: fieldDetails[1] === 'Y',
          value: getFieldDefaultValue(detailId),
          apiInitialValue: getFieldDefaultValue(detailId),
        };

        if (detailId === 'iban') {
          newField.value = iban;
          newField.apiInitialValue = iban;
        }

        if (detailId === 'ibanUsd') {
          newField.value = iban2;
          newField.apiInitialValue = iban2;
        }

        if (detailId === 'capitalPctg' || detailId.startsWith('partner')) {
          const legalPersonPartnersField = acc.find(
            (accField) => accField.name === 'partners',
          );

          if (legalPersonPartnersField) {
            legalPersonPartnersField.data[0].push(newField);
          } else {
            acc.push({
              name: 'partners',
              data: [[newField]],
            });
          }

          return acc;
        }

        if (
          detailId === 'representative' ||
          detailId === 'beneficiary' ||
          detailId.startsWith('rep')
        ) {
          const legalPersonRepresentativesField = acc.find(
            (accField) => accField.name === 'representatives',
          );

          if (legalPersonRepresentativesField) {
            legalPersonRepresentativesField.data[0].push(newField);
          } else {
            acc.push({
              name: 'representatives',
              data: [[newField]],
            });
          }

          return acc;
        }

        acc.push(newField);

        return acc;
      }, []);

      const mainPersonInfo = formPreviousInfo.find((user) => user.isMainPerson);
      const otherPersons = formPreviousInfo.filter(
        (user) => !user.isMainPerson,
      );

      state.onboarding.mainFormFields = fields.map((field) => {
        if (field.name === 'representatives' && otherPersons.length > 0) {
          field.data = field.data.map((rep, index) =>
            rep.map((repField) => {
              repField.value = getFieldValue(repField, otherPersons[index]);
              repField.apiInitialValue = getFieldValue(
                repField,
                otherPersons[index],
              );
              return repField;
            }),
          );
        } else if (mainPersonInfo) {
          field.value = getFieldValue(field, mainPersonInfo);
          field.apiInitialValue = getFieldValue(field, mainPersonInfo);
        }

        return field;
      });
    },
    setMainFormValues: (state, action) => {
      const updatedFields = state.onboarding.mainFormFields.map((field) => {
        if (field.data) {
          const elements = action.payload[field.name].map((formFields) => {
            const fields = Object.keys(formFields).map((formField) => {
              const completeField = field.data[0].find(
                (subField) => subField.name === formField,
              );

              return { ...completeField, value: formFields[formField] };
            });

            return fields;
          });

          return {
            ...field,
            data: elements,
          };
        }

        return {
          ...field,
          value: action.payload[field.name],
        };
      });

      state.onboarding.mainFormFields = updatedFields;
    },
    getClassifDescriptionsRequest: (state) => state,
    getClassifDescriptionsSuccess: (state, action) => {
      const groupedClassifiers = action.payload.reduce((acc, classifier) => {
        // eslint-disable-next-line unused-imports/no-unused-vars
        const { type, id, classifierId, ...classifierOption } = classifier;

        const formattedClassifierOption = {
          label: classifierOption.description,
          value: classifierOption.classifierValue,
        };

        const currentClassifierIndex = acc.findIndex(
          (groupClass) => groupClass.id === classifier.classifierId,
        );

        if (currentClassifierIndex === -1) {
          acc.push({
            id: classifier.classifierId,
            options: [formattedClassifierOption],
          });

          return acc;
        }

        acc[currentClassifierIndex].options.push(formattedClassifierOption);
        return acc;
      }, []);

      const updateMainFormFields = state.onboarding.mainFormFields.map(
        (field) => {
          if (field.name === 'idType') {
            field.options = [
              ...getClassifierOptions({
                classifiers: groupedClassifiers,
                currentClassifierId: 'TRLT_DOCIDENTPS',
              }),
              ...(isStinvestActiveClient
                ? [{ label: 'TRLT_UI-OTHER', value: 'OUTRO' }]
                : []),
            ];
          } else if (field.name === 'profStatus') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_ESTPROF',
            });
          } else if (field.name === 'educQualifications') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_HABLIT',
            });
          } else if (field.name === 'incomeCurrency') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_DIVACTIVO',
            });
          } else if (field.name === 'incomeCurrencyDestination') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_DIVACTIVO',
            });
          } else if (field.name === 'incomeSource') {
            field.options = [
              ...getClassifierOptions({
                classifiers: groupedClassifiers,
                currentClassifierId: 'TRLT_ORIGREND',
              }),
              ...(isStinvestActiveClient
                ? [{ label: 'TRLT_UI-OTHER', value: 'OUTRO' }]
                : []),
            ];
          } else if (field.name === 'employeeType') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_TIPOTRAB',
            });
          } else if (field.name === 'representatives') {
            const updatedFieldData = field.data.map((representative) => {
              const updatedRep = representative.map((repField) => {
                if (repField.name === 'repIdType') {
                  repField.options = getClassifierOptions({
                    classifiers: groupedClassifiers,
                    currentClassifierId: 'TRLT_DOCIDENTPC',
                  });

                  return repField;
                }

                if (repField.name === 'repIncomeSource')
                  repField.options = getClassifierOptions({
                    classifiers: groupedClassifiers,
                    currentClassifierId: 'TRLT_NATRENDPC',
                  });

                return repField;
              });

              return updatedRep;
            });

            field.data = updatedFieldData;
          } else if (field.name === 'maritalStatus') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_ESTCIVIL',
            });
          } else if (field.name === 'regime') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_REGIME',
            });
          } else if (field.name === 'incomeValues') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_VALREND',
            });
          } else if (field.name === 'mainDwelling') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_HABPRINC',
            });
          } else if (field.name === 'car') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_AUTO',
            });
          } else if (field.name === 'realEstateTotalValue') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_VALIMOB',
            });
          } else if (field.name === 'movableAssetsTotalValue') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_VALMOB',
            });
          } else if (field.name === 'society') {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_SOC',
            });
          } else if (field.name === 'relationAssets') {
            const relationAssetsOptions = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_DESTFUND',
            });
            const fieldInitialValue = relationAssetsOptions.map(
              (option) => option.value,
            );

            field.options = relationAssetsOptions;
            field.value = fieldInitialValue;
            field.apiInitialValue = fieldInitialValue;
          } else if (field.name === 'job' && isStinvestActiveClient) {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_PROFISSAO',
              shouldSortAlphabetically: true,
            });
          } else if (
            (field.name === 'country' ||
              field.name === 'placeOfBirth' ||
              field.name === 'nationality' ||
              field.name === 'otherNationalities') &&
            isStinvestActiveClient
          ) {
            field.options = getClassifierOptions({
              classifiers: groupedClassifiers,
              currentClassifierId: 'TRLT_PAISES',
              shouldSortAlphabetically: true,
            });
          }

          return field;
        },
      );

      state.onboarding.mainFormFields = updateMainFormFields;
    },
    getClassifDescriptionsFailure: (state) => state,
    getNewAccountRequest: (state) => state,
    getNewAccountInfoSuccess: (state, action) => {
      const {
        openingRequestId,
        legalStatus,
        ibanCode,
        ibanCode2,
        bankAccountNumber,
        answeredSurvey,
        holderCode,
        hasQuestionnaire,
        hasOnboardingInitialOtp,
        userInitialOtpDelivMetds,
      } = action.payload;

      if (hasOnboardingInitialOtp) {
        state.onboarding = {
          ...state.onboarding,
          userInitialOtpDelivMetds,
        };
      } else {
        const isQuestionnaireAvailable =
          answeredSurvey === 'N' && hasQuestionnaire;

        state.onboarding = {
          ...state.onboarding,
          legalStatus,
          shouldShowQuestionnaire: isQuestionnaireAvailable,
          holderCode,
        };
      }

      state.onboarding = {
        ...state.onboarding,
        openingRequestId,
        ibanCode,
        ibanCode2,
        bankAccountNumber,
      };
    },
    getNewAccountInfoFailure: (state, action) => {
      state.onboarding.error = action.payload[0].detail;
    },
    getDocumentsListRequest: (state) => state,
    getDocumentsListSuccess: (state, action) => {
      const { documents, hasDocumentMandatoryAndIdentification } =
        action.payload;

      state.onboarding.documents.data = documents;
      state.onboarding.documents.mandatoryAndIdentification =
        hasDocumentMandatoryAndIdentification;
    },
    getDocumentsListFailure: (state, action) => {
      state.onboarding.error = action.payload[0]?.detail;
    },
    removeDocument: (state, action) => {
      const { index } = action.payload;
      if (state.onboarding.documents.data[index])
        state.onboarding.documents.data[index].values = null;
    },
    submitDocumentRequest: (state) => state,
    submitDocumentSuccess: (state, action) => {
      const { values, fieldName, deleteDoc, index } = action.payload;
      if (deleteDoc === 'S') {
        delete state.onboarding.documents.data[index].values;
      } else {
        state.onboarding.documents.data[index].values = {
          ...values,
        };
        state.onboarding.documents.feedback = {
          type: 'success',
          message: 'message',
          isOpen: true,
          filename: values.filename,
          fieldName,
        };
      }
    },
    submitDocumentFailure: (state, action) => {
      const { error, filename, fieldName, key } = action.payload;
      state.onboarding.documents.feedback = {
        type: 'error',
        message: error,
        isOpen: true,
        filename,
        fieldName,
        key,
      };
    },
    clearDocumentFeedback: (state) => {
      state.onboarding.documents.feedback.isOpen = false;
    },
    submitFinancialContractRequest: (state) => state,
    submitFinancialContractFailure: (state, action) => {
      state.onboarding.error = action.payload[0].detail;
    },
    generateContractPdfRequest: (state) => {
      state.onboarding.contract.isLoading = true;
    },
    generateContractPdfSuccess: (state, action) => {
      const { file } = action.payload;
      state.onboarding.contract.isLoading = false;
      state.onboarding.contract.file = file ?? '';
    },
    generateContractPdfFailure: (state, action) => {
      state.onboarding.contract.isLoading = false;
      state.onboarding.error = action.payload[0]?.detail
        ? `${action.payload[0]?.detail} TRLT_UI-REDIRECTION_NOTICE`
        : 'TRLT_UI-GENERATE_CONTRACT_ERROR_MSG';
    },
    setCreateAccountDisclaimer: (state, action) => {
      const { disclaimer } = action.payload;
      state.onboarding.createAccountDisclaimer = disclaimer;
    },
    setContractDisclaimer: (state, action) => {
      const { disclaimer } = action.payload;
      state.onboarding.contract.disclaimer = disclaimer;
    },
    setOnboardingStateRequest: (state) => state,
    setOnboardingStateSuccess: (state, action) => {
      if (!action.payload.hasInitialOtpValidation)
        state.onboarding.isJoinProcessCompleted = true;
    },
    setOnboardingStateFailure: (state, action) => {
      state.onboarding.error = action.payload[0].detail;
    },
    setOnboardingError: (state, action) => {
      state.onboarding.error = action.payload[0].detail;
    },
    getLegalStatusListRequest: (state) => state,
    getLegalStatusListSuccess: (state, action) => {
      const { legalStatusList } = action.payload;

      state.onboarding.legalStatusList = legalStatusList;
    },
    getLegalStatusListFailure: (state, action) => {
      state.onboarding.error = action.payload[0]?.detail;
    },
    setLegalStatus: (state, action) => {
      const { legalStatus } = action.payload;
      state.onboarding.legalStatus = legalStatus;
    },
  },
});
